import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.dark,
  boxShadow: 'none',
  position: 'fixed',
  transition: 'background-color 0.3s ease',
  '&.scrolled': {
    backgroundColor: theme.palette.background.dark,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
  },
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: '56px !important',
  padding: '0 !important',
});

const StyledButton = styled(Button)(({ theme }) => ({
  color: theme.palette.common.white,
  fontSize: '0.9rem',
  fontWeight: 500,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  '&.active': {
    position: 'relative',
    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 16,
      right: 16,
      height: '2px',
      backgroundColor: theme.palette.common.white,
    },
  },
}));

const Logo = styled('img')(({ theme }) => ({
  height: '44px',
  [theme.breakpoints.down('sm')]: {
    height: '36px',
  },
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 0.9,
  },
}));

const pages = [
  { name: 'Home', path: '/' },
  { name: 'Our Fleet', path: '/fleet' },
  { name: 'Our Services', path: '/services' },
  { name: 'Make a Booking', path: '/booking' },
  { name: 'FAQ & Terms', path: '/faq-terms' },
];

const Navbar = () => {
  const [anchorElNav, setAnchorElNav] = useState(null);
  const location = useLocation();
  const theme = useTheme();

  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <StyledAppBar>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <StyledToolbar>
          {/* Desktop Logo */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 3 }}>
            <RouterLink to="/">
              <Logo
                src="/images/logo/eminent-logo-white.png"
                alt="Eminent Chauffeurs"
              />
            </RouterLink>
          </Box>

          {/* Mobile Menu */}
          <Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="small"
              aria-label="menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              sx={{ color: 'common.white', p: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiPaper-root': {
                  backgroundColor: 'background.dark',
                  color: 'common.white',
                },
              }}
            >
              {pages.map((page) => (
                <MenuItem
                  key={page.name}
                  onClick={handleCloseNavMenu}
                  component={RouterLink}
                  to={page.path}
                  selected={location.pathname === page.path}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Typography textAlign="center">
                    {page.name}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Mobile Logo */}
          <Box sx={{ 
            flexGrow: 1, 
            display: { xs: 'flex', md: 'none' }, 
            justifyContent: 'center',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <RouterLink to="/">
              <Logo
                src="/images/logo/eminent-logo-white.png"
                alt="Eminent Chauffeurs"
              />
            </RouterLink>
          </Box>

          {/* Desktop Menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            {pages.map((page) => (
              <StyledButton
                key={page.name}
                component={RouterLink}
                to={page.path}
                className={location.pathname === page.path ? 'active' : ''}
                onClick={handleCloseNavMenu}
                sx={{ py: 1 }}
              >
                {page.name}
              </StyledButton>
            ))}
          </Box>
        </StyledToolbar>
      </Container>
    </StyledAppBar>
  );
};

export default Navbar; 